/**
 * Created by Alican Köker on 2018-04-12.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {BFGameEvent} from "../events/BFGameEvent";
import {BarbarianFuryGameMode} from "../gamemode/BarbarianFuryGameMode";

export interface MHCounterViewData {
    imageAssetPrefix: string,
    digits: number,
    frameHeight: number,
    frameWidth: number,
    value?: number,
    imageLeftMargin?: number,
    imageTopMargin?: number,
    digitPadding?: number,
    animationDuration?: number,
    maskWidth?: number,
    maskHeight?: number,
    maskX?: number,
    maskY?: number,
}

interface digit {
    container: PIXI.Container,
    value: number,
    position: number,
    targetVal?: number,
}

export class MultiplierHolderCounterView extends BaseView {
    public _digitArr: digit[] = [];
    private _digits: number; //number of total digits on the counter
    private _frameHeight: number = 0;
    private _frameWidth: number = 0;
    private _digitPadding: number = 0;
    private _imageNames: string[] = [];
    private _digitContainer: PIXI.Container;
    private _value: number = 0;
    public _animationSpeed: number = 0;
    private _maskWidth: number = 0;
    private _maskHeight: number = 0;
    private _maskX: number = 0;
    private _maskY: number = 0;
    private _gameMode: string = BarbarianFuryGameMode.NORMAL;

    constructor(data: MHCounterViewData) {
        super();
        this.initData(data);
    }

    public setCounterValue(newValue: number): void {
        let multiplierLength = newValue.toString().length;
        if (multiplierLength > this._digits) {
            this._digits = multiplierLength;
            this._digitArr[multiplierLength - 1] = {
                container: new PIXI.Container(),
                value: 0,
                position: multiplierLength - 1,
                targetVal: 0
            };
            this.initDigits();
        }
        for (let i: number = 0; i < this._digits - newValue.toString().length; i++) {
            this._digitArr[i].targetVal = 0;
            this.setDigitValue(0, i);
        }
        for (let i: number = this._digits - newValue.toString().length; i < this._digits; i++) {
            let v = Number(newValue.toString()[i - (this._digits - newValue.toString().length)]);
            this._digitArr[i].targetVal = v;
            this.setDigitValue(v, i);
        }
    }

    protected addEventListeners(): void {
        EventHandler.addEventListener(this, BFGameEvent.UPDATE_MULTIPLIER_BG, (event: GameEvent) => this.updateMultiplierGraphics(event.params[0]));
    }

    protected initAnimations(): void {
        this.initDigits();

        let mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFFFFFF, 1);
        mask.drawRect(this._maskX, this._maskY, this._maskWidth, this._maskHeight);
        mask.endFill();
        this.addChild(mask);

        this.mask = mask;
    }

    private updateMultiplierGraphics(src: string): void {
        if (src.indexOf("mainGame") > -1) {
            this._gameMode = BarbarianFuryGameMode.NORMAL;
        } else {
            this._gameMode = BarbarianFuryGameMode.FREESPIN;
            this._digitArr[0].container.visible = true;
        }
    }

    private initData(data: MHCounterViewData): void {
        this._digits = data.digits;
        this._frameHeight = data.frameHeight;
        this._frameWidth = data.frameWidth;
        this._maskWidth = data.maskWidth!;
        this._maskHeight = data.maskHeight!;
        this._maskX = data.maskX!;
        this._maskY = data.maskY!;
        this._digitArr = new Array(this._digits);
        this._digitPadding = data.digitPadding!;
        this._value = data.value!;
        this._animationSpeed = data.animationDuration!;

        for (let i: number = 0; i < 10; i++) {
            this._imageNames.push(data.imageAssetPrefix + i.toString())
        }
        for (let i: number = data.digits - 1; i >= 0; i--) {
            this._digitArr[i] = {
                container: new PIXI.Container(),
                value: 0,
                position: i,
                targetVal: 0,
            };
        }
        this._digitContainer = this._digitArr[0].container;
    }

    private initDigits(): void {
        for (let i: number = 0; i < this._digits - this._value.toString().length; i++) {
            this.updateDigit(0, i);
            this._digitArr[i].container.position.x = i * (this._frameWidth + this._digitPadding);
            this._digitArr[i].value = 0;
            this._digitArr[i].targetVal = 0;
            this.addChild(this._digitArr[i].container);
        }
        for (let i: number = this._digits - this._value.toString().length; i < this._digits; i++) {
            let v: number = Number(this._value.toString()[i - (this._digits - this._value.toString().length)]);
            this.updateDigit(v, i);
            this._digitArr[i].container.position.x = i * (this._frameWidth + this._digitPadding);
            this._digitArr[i].value = v;
            this._digitArr[i].targetVal = v;
            this.addChild(this._digitArr[i].container);
        }
    }

    private updateDigit(value: number, digitPos?: number): void {
        if (!digitPos) digitPos = 0;
        this._digitArr[digitPos].value = value;
        if (this._digitArr[digitPos].container.children.length > 0)
            this._digitArr[digitPos].container.removeChildren();

        //0, 1, 8 and 9 are special cases since they are on the both ends of the counting stripe
        if (value === 0) {
            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[8])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, 2 * this._frameHeight);

            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[9])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, this._frameHeight);

            for (let i: number = 0; i < 3; i++) {
                this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[i])[0]);
                this._digitArr[digitPos].container.addChild(this._digitContainer);
                this._digitContainer.position.set(0, -(i - value) * this._frameHeight);
            }
        } else if (value === 1) {
            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[8])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, 2 * this._frameHeight);

            for (let i: number = 0; i < 4; i++) {
                this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[i])[0]);
                this._digitArr[digitPos].container.addChild(this._digitContainer);
                this._digitContainer.position.set(0, -(i - value) * this._frameHeight);
            }
        } else if (value === 8) {
            for (let i: number = 6; i < 10; i++) {
                this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[i])[0]);
                this._digitArr[digitPos].container.addChild(this._digitContainer);
                this._digitContainer.position.set(0, -(i - value) * this._frameHeight);
            }

            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[0])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, -2 * this._frameHeight);

        } else if (value === 9) {
            for (let i: number = 7; i < 10; i++) {
                let digitNumber: PIXI.Sprite = new PIXI.Sprite(GameResources.getTextures(this._imageNames[i])[0]);
                this._digitArr[digitPos].container.addChild(digitNumber);
                digitNumber.position.set(0, -(i - value) * this._frameHeight);
            }

            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[0])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, -this._frameHeight);

            this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[1])[0]);
            this._digitArr[digitPos].container.addChild(this._digitContainer);
            this._digitContainer.position.set(0, -2 * this._frameHeight);
        } else {
            for (let i: number = value - 2; i < value + 3; i++) {
                this._digitContainer = new PIXI.Sprite(GameResources.getTextures(this._imageNames[i])[0]);
                this._digitArr[digitPos].container.addChild(this._digitContainer);
                this._digitContainer.position.set(0, -(i - value) * this._frameHeight);
            }
        }
    }

    private incrementDigit(digitPos: number, duration?: number, complete?: () => void): void {
        new TweenLite(this._digitArr[digitPos].container, duration!,
            {
                y: this._frameHeight,
                ease: Linear.easeNone,
                onComplete: () => {
                    if ((this._digitArr[digitPos].value + 1) < 10) {
                        this._digitArr[digitPos].value++;
                        this._digitArr[digitPos].container.y = 0;
                    } else {
                        this._digitArr[digitPos].value = 0;
                        this._digitArr[digitPos].container.y = 0;
                    }
                    this.updateDigit(this._digitArr[digitPos].value, digitPos);

                    if (complete) complete();
                }
            });
    }

    private decrementDigit(digitPos: number, duration?: number, complete?: () => void): void {
        new TweenLite(this._digitArr[digitPos].container, duration!,
            {
                y: -this._frameHeight,
                ease: Linear.easeNone,
                onComplete: () => {
                    if ((this._digitArr[digitPos].value) > 0) {
                        this._digitArr[digitPos].value--;
                        this._digitArr[digitPos].container.y = 0;
                    } else {
                        this._digitArr[digitPos].value = 9;
                        this._digitArr[digitPos].container.y = 0;
                    }
                    this.updateDigit(this._digitArr[digitPos].value, digitPos);

                    if (complete) complete();
                }
            });
    }

    private setDigitValue(newValue: number, digitPos: number, onComplete?: () => {}) {
        let animSp: number = this._digitArr[digitPos].targetVal! !== 0 ? this._animationSpeed / this._digitArr[digitPos!].targetVal! : this._animationSpeed;

        if (newValue === 0 && this._digitArr[digitPos].value === 9) {
            new TweenLite(this._digitArr[digitPos].container, animSp,
                {
                    y: this._frameHeight,
                    ease: Linear.easeNone,
                    onComplete: () => {
                        this._digitArr[digitPos].value = 0;
                        this._digitArr[digitPos].container.y = 0;
                        this.updateDigit(0, digitPos);

                        if (onComplete) onComplete();
                    }
                });

        } else if (newValue === 9 && this._digitArr[digitPos].value === 0) {
            new TweenLite(this._digitArr[digitPos].container, animSp,
                {
                    y: -this._frameHeight,
                    ease: Linear.easeNone,
                    onComplete: () => {
                        this._digitArr[digitPos].value = 9;
                        this._digitArr[digitPos].container.y = 0;
                        this.updateDigit(9, digitPos);

                        if (onComplete) onComplete();
                    }
                });
        } else if (newValue > this._digitArr[digitPos].value) {
            this.incrementDigit(digitPos!, animSp, () => {
                if (newValue !== this._digitArr[digitPos].value - 1) {
                    this.setDigitValue(newValue, digitPos);
                }
            })
        } else if (newValue < this._digitArr[digitPos].value) {
            this.decrementDigit(digitPos!, animSp, () => {
                if (newValue !== this._digitArr[digitPos].value) {
                    this.setDigitValue(newValue, digitPos);
                }
            })
        }
    }
}